/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// src/components/Header.tsx
import React, { useEffect, useMemo, useState } from "react";
import { AppBar, Typography, Box, Button, MenuItem, Menu, IconButton, Tooltip } from "__barrel_optimize__?names=AppBar,Box,Button,IconButton,Menu,MenuItem,Tooltip,Typography!=!@mui/material";
import { styled } from "__barrel_optimize__?names=styled!=!@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Link from "next/link";
import { tokens } from "../theme";
import { SvgIcon } from "__barrel_optimize__?names=SvgIcon!=!@mui/material";
import Image from "next/image";
import { appStyled } from "../utilityFunctions";
import { signOut, useSession } from "next-auth/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import GermanFlag from "../../../../public/ger.png";
import BritainFlag from "../../../../public/eng.png";
import { useParams, usePathname, useRouter } from "next/navigation";
import { fallbackLng, germanLng } from "../../../i18n/settings";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import useSubscriptionStore from "../store/subscriptionStore";
import SubscriptionWarningModal from "../components/SubscriptionWarningModal";
import { useTranslation } from "../../../i18n/client";
const getCallbackUrl = ()=>{
    const base = process.env.NEXT_PUBLIC_CLIENT_URL;
    if (!base) {
        return "/api/auth/login";
    }
    return "".concat(base, "/api/auth/login");
};
const StyledLink = styled(Link)({
    textDecoration: "inherit",
    color: "inherit"
});
const Wrapper = styled(AppBar)((param)=>{
    let { theme } = param;
    return {
        boxShadow: "0px 4px 15px 0px rgba(0, 0, 0, 0.05)",
        top: 0,
        color: "#FFF",
        zIndex: 1000,
        position: "sticky",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        height: "72px",
        paddingLeft: "50px",
        paddingRight: "50px",
        gap: "20px",
        backgroundColor: "#FFF",
        [theme.breakpoints.down("lg")]: {
            height: "52px",
            paddingLeft: "20px",
            paddingRight: "20px"
        }
    };
});
const LogoIcon = styled("span")((param)=>{
    let { theme } = param;
    return {
        display: "inline-flex",
        width: "180px",
        height: "50.4px",
        [theme.breakpoints.down("lg")]: {
            width: "140px",
            height: "39.2px"
        },
        [theme.breakpoints.down("sm")]: {
            width: "120px",
            height: "33.6px"
        },
        "& svg": {
            display: "block",
            width: "100%",
            height: "100%"
        }
    };
});
const AccountIcon = styled(SvgIcon)((param)=>{
    let { theme } = param;
    return {
        width: "20px",
        height: "20px",
        [theme.breakpoints.down("sm")]: {
            height: "28px",
            width: "28px"
        }
    };
});
const StyledMenu = styled((props)=>/*#__PURE__*/ _jsx(Menu, {
        elevation: 0,
        anchorOrigin: {
            vertical: "bottom",
            horizontal: "right"
        },
        transformOrigin: {
            vertical: "top",
            horizontal: "right"
        },
        ...props
    }))((param)=>{
    let { theme } = param;
    return {
        "& .MuiPaper-root": {
            borderRadius: 10,
            boxShadow: "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
            "& .MuiMenu-list": {
                padding: "4px 0"
            }
        }
    };
});
const StyledLanguageButton = styled(Button)((param)=>{
    let { theme } = param;
    return {
        backgroundColor: "transparent",
        fontSize: "14px",
        fontWeight: "400",
        "&:hover": {
            backgroundColor: "transparent"
        }
    };
});
const MobileNavigationIcon = styled(SvgIcon)({
    width: "25px",
    height: "18px"
});
const WrapperNavigation = appStyled(Box)((param)=>{
    let { theme, $isNotNavigation } = param;
    return {
        display: "flex",
        gap: "30px",
        ...!$isNotNavigation && {
            marginLeft: "36px"
        },
        [theme.breakpoints.down("lg")]: {
            ...!$isNotNavigation ? {
                marginLeft: "23px",
                gap: "25px"
            } : {
                gap: "20px"
            }
        }
    };
});
const NavigationText = appStyled(Typography)((param)=>{
    let { theme, $isActive } = param;
    return {
        textTransform: "uppercase",
        ...$isActive && {
            fontWeight: "700"
        },
        [theme.breakpoints.down("lg")]: {
            fontSize: "12px"
        }
    };
});
const DrawerText = appStyled(Typography)((param)=>{
    let { theme, $isActive } = param;
    return {
        textTransform: "uppercase",
        ...$isActive && {
            fontWeight: "700"
        }
    };
});
const Header = ()=>{
    var _params_lng, _session_user, _session_user1, _params_lng1;
    const params = useParams();
    const router = useRouter();
    const pathname = usePathname();
    var _params_lng_toString;
    const lng = (_params_lng_toString = params === null || params === void 0 ? void 0 : (_params_lng = params.lng) === null || _params_lng === void 0 ? void 0 : _params_lng.toString()) !== null && _params_lng_toString !== void 0 ? _params_lng_toString : "en";
    const { t } = useTranslation(lng, "header");
    const [startIcon, setStartIcon] = useState(params && params.lng === "de" ? /*#__PURE__*/ _jsx(Image, {
        src: GermanFlag,
        alt: "deu",
        width: "21",
        height: "12"
    }) : /*#__PURE__*/ _jsx(Image, {
        src: "/eng.png",
        alt: "eng",
        width: "21",
        height: "12"
    }));
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isTablet = useMediaQuery(theme.breakpoints.up("md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const headerLinks = {
        home: "home",
        customers: "customers",
        invoices: "invoices",
        products: "products",
        xrechnung: "xrechnung",
        support: "support",
        myaccount: "myaccount",
        logout: "logout"
    };
    const [activeHeader, setActiveHeader] = useState(headerLinks.home);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [showDrawer, setShowDrawer] = useState(false);
    const open = Boolean(anchorEl);
    const { plan, isExpired, daysLeft, invoicesRemaining } = useSubscriptionStore();
    const { data: session, status: sessionStatus } = useSession();
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const getEmailFromToken = (token)=>{
        if (!token || "object" === "undefined" || typeof window.atob !== "function") {
            return undefined;
        }
        try {
            const payloadSegment = token.split(".")[1];
            if (!payloadSegment) return undefined;
            const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
            const decodedPayload = window.atob(base64);
            const jsonPayload = JSON.parse(decodedPayload);
            var _jsonPayload_email, _ref, _ref1, _ref2;
            return (_ref2 = (_ref1 = (_ref = (_jsonPayload_email = jsonPayload === null || jsonPayload === void 0 ? void 0 : jsonPayload.email) !== null && _jsonPayload_email !== void 0 ? _jsonPayload_email : jsonPayload === null || jsonPayload === void 0 ? void 0 : jsonPayload.user_email) !== null && _ref !== void 0 ? _ref : jsonPayload === null || jsonPayload === void 0 ? void 0 : jsonPayload.userEmail) !== null && _ref1 !== void 0 ? _ref1 : jsonPayload === null || jsonPayload === void 0 ? void 0 : jsonPayload.sub) !== null && _ref2 !== void 0 ? _ref2 : undefined;
        } catch (error) {
            console.warn("Failed to decode user email from token", error);
            return undefined;
        }
    };
    const userEmailTooltip = useMemo(()=>{
        var _session_user, _session_user1;
        if (session === null || session === void 0 ? void 0 : (_session_user = session.user) === null || _session_user === void 0 ? void 0 : _session_user.email) {
            return session.user.email;
        }
        const tokenEmail = getEmailFromToken(session === null || session === void 0 ? void 0 : (_session_user1 = session.user) === null || _session_user1 === void 0 ? void 0 : _session_user1.token);
        if (tokenEmail) {
            return tokenEmail;
        }
        if (sessionStatus === "loading") {
            return t("user_email_loading");
        }
        if (sessionStatus === "authenticated") {
            return t("user_email_unavailable");
        }
        return "";
    }, [
        session === null || session === void 0 ? void 0 : (_session_user = session.user) === null || _session_user === void 0 ? void 0 : _session_user.email,
        session === null || session === void 0 ? void 0 : (_session_user1 = session.user) === null || _session_user1 === void 0 ? void 0 : _session_user1.token,
        sessionStatus,
        t
    ]);
    const handleClick = (event)=>{
        setAnchorEl(event.currentTarget);
    };
    const handleMobileNavigation = (headerLink)=>{
        headerLink && setActiveHeader(headerLink);
        setTimeout(()=>{
            setShowDrawer(false);
        }, 200);
    };
    const handleXrechnungNavigation = function(event) {
        let isMobileNav = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
        if (isExpired) {
            event.preventDefault();
            event.stopPropagation();
            setShowSubscriptionModal(true);
            return;
        }
        if (isMobileNav) {
            handleMobileNavigation(headerLinks.xrechnung);
        } else {
            setActiveHeader(headerLinks.xrechnung);
        }
    };
    const handleClose = (lng)=>{
        setAnchorEl(null);
        if (lng === fallbackLng) {
            setStartIcon(/*#__PURE__*/ _jsx(Image, {
                src: BritainFlag,
                alt: fallbackLng,
                width: "21",
                height: "12"
            }));
            router.push("/".concat(fallbackLng) + (pathname === null || pathname === void 0 ? void 0 : pathname.substring(3)));
        } else if (lng === germanLng) {
            setStartIcon(/*#__PURE__*/ _jsx(Image, {
                src: GermanFlag,
                alt: germanLng,
                width: "21",
                height: "12"
            }));
            router.push("/".concat(germanLng) + (pathname === null || pathname === void 0 ? void 0 : pathname.substring(3)));
        }
    };
    const handleSignOut = async ()=>{
        await signOut({
            redirect: true,
            callbackUrl: getCallbackUrl()
        });
    // await signOut({ redirect: false }); // Set redirect to false to handle redirection manually
    // router.push(`${process.env.NEXT_PUBLIC_CLIENT_URL}`); // Redirect to the desired page after signing out
    // router.refresh();
    };
    const handleUpgradeNowClick = ()=>{
        setActiveHeader(headerLinks.myaccount);
        router.push("/".concat(lng, "/application/myaccount?tab=my-plan"));
    };
    const renderSubscriptionTooltip = ()=>{
        if (!plan) {
            return t("loading_subscription");
        }
        if (isExpired) {
            return /*#__PURE__*/ _jsxs(Box, {
                display: "flex",
                flexDirection: "column",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        color: "inherit",
                        children: t("subscription_expired")
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        sx: {
                            mt: 0.5,
                            color: "#FF9F24",
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline"
                        },
                        onClick: handleUpgradeNowClick,
                        children: t("free_trial_expires_cta")
                    })
                ]
            });
        }
        if (plan === "Free") {
            return /*#__PURE__*/ _jsxs(Box, {
                display: "flex",
                flexDirection: "column",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        color: "inherit",
                        children: t("free_trial_expires", {
                            daysLeft
                        })
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        sx: {
                            mt: 0.5,
                            color: "#FF9F24",
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline"
                        },
                        onClick: handleUpgradeNowClick,
                        children: t("free_trial_expires_cta")
                    })
                ]
            });
        }
        if (invoicesRemaining === "unlimited") {
            return t("unlimited_invoices");
        }
        if (typeof invoicesRemaining === "number" && invoicesRemaining <= 0) {
            const fullText = t("invoice_limit_reached", {
                daysLeft
            });
            const upgradeWord = t("invoice_limit_reached_upgrade_word");
            // Split only on the first occurrence of the upgrade word
            const upgradeIndex = fullText.indexOf(upgradeWord);
            if (upgradeIndex === -1) {
                // Fallback to simple text if the word isn't found
                return fullText;
            }
            const beforeText = fullText.slice(0, upgradeIndex);
            const afterText = fullText.slice(upgradeIndex + upgradeWord.length);
            return /*#__PURE__*/ _jsx(Box, {
                display: "flex",
                flexDirection: "column",
                children: /*#__PURE__*/ _jsxs(Typography, {
                    variant: "body2",
                    color: "inherit",
                    children: [
                        beforeText,
                        /*#__PURE__*/ _jsx(Typography, {
                            component: "span",
                            sx: {
                                color: "#FF9F24",
                                fontWeight: 600,
                                cursor: "pointer",
                                textDecoration: "underline"
                            },
                            onClick: handleUpgradeNowClick,
                            children: upgradeWord
                        }),
                        afterText
                    ]
                })
            });
        }
        return t("invoices_remaining", {
            count: invoicesRemaining
        });
    };
    useEffect(()=>{
        const applicationRoot = "/".concat(lng, "/application");
        if (pathname === applicationRoot) {
            setActiveHeader(headerLinks.home);
            return;
        }
        if (pathname === null || pathname === void 0 ? void 0 : pathname.startsWith("".concat(applicationRoot, "/customers"))) {
            setActiveHeader(headerLinks.customers);
            return;
        }
        if (pathname === null || pathname === void 0 ? void 0 : pathname.startsWith("".concat(applicationRoot, "/invoices"))) {
            setActiveHeader(headerLinks.invoices);
            return;
        }
        if (pathname === null || pathname === void 0 ? void 0 : pathname.startsWith("".concat(applicationRoot, "/products"))) {
            setActiveHeader(headerLinks.products);
            return;
        }
        if (pathname === null || pathname === void 0 ? void 0 : pathname.startsWith("".concat(applicationRoot, "/xrechnung-visu"))) {
            setActiveHeader(headerLinks.xrechnung);
            return;
        }
        if (pathname === null || pathname === void 0 ? void 0 : pathname.startsWith("".concat(applicationRoot, "/myaccount"))) {
            setActiveHeader(headerLinks.myaccount);
            return;
        }
        setActiveHeader("");
    }, [
        pathname,
        lng
    ]);
    var _params_lng_toString1;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Wrapper, {
                children: [
                    /*#__PURE__*/ _jsxs(Box, {
                        display: "flex",
                        alignItems: "center",
                        children: [
                            isMobile && (!showDrawer ? /*#__PURE__*/ _jsx(IconButton, {
                                disableRipple: true,
                                sx: {
                                    marginRight: "15px"
                                },
                                onClick: ()=>setShowDrawer(true),
                                children: /*#__PURE__*/ _jsxs(MobileNavigationIcon, {
                                    children: [
                                        /*#__PURE__*/ _jsx("rect", {
                                            width: "25",
                                            height: "2",
                                            rx: "1",
                                            fill: "black"
                                        }),
                                        /*#__PURE__*/ _jsx("rect", {
                                            y: "8",
                                            width: "25",
                                            height: "2",
                                            rx: "1",
                                            fill: "black"
                                        }),
                                        /*#__PURE__*/ _jsx("rect", {
                                            y: "16",
                                            width: "25",
                                            height: "2",
                                            rx: "1",
                                            fill: "black"
                                        })
                                    ]
                                })
                            }) : /*#__PURE__*/ _jsx(IconButton, {
                                disableRipple: true,
                                sx: {
                                    marginRight: "15px"
                                },
                                onClick: ()=>setShowDrawer(false),
                                children: /*#__PURE__*/ _jsxs(MobileNavigationIcon, {
                                    children: [
                                        /*#__PURE__*/ _jsx("rect", {
                                            x: "2.12109",
                                            y: "0.000183105",
                                            width: "31",
                                            height: "3",
                                            transform: "rotate(45 2.12109 0.000183105)",
                                            fill: "black"
                                        }),
                                        /*#__PURE__*/ _jsx("rect", {
                                            x: "24.042",
                                            y: "2.12134",
                                            width: "31",
                                            height: "3",
                                            transform: "rotate(135 24.042 2.12134)",
                                            fill: "black"
                                        })
                                    ]
                                })
                            })),
                            /*#__PURE__*/ _jsx(Box, {
                                children: /*#__PURE__*/ _jsx(StyledLink, {
                                    href: "/".concat(lng, "/application"),
                                    onClick: ()=>setActiveHeader(headerLinks.home),
                                    sx: {
                                        cursor: "pointer",
                                        display: "inline-block"
                                    },
                                    children: /*#__PURE__*/ _jsx(LogoIcon, {
                                        children: /*#__PURE__*/ _jsx("svg", {
                                            xmlns: "http://www.w3.org/2000/svg",
                                            viewBox: "0 0 1355.78 202.53",
                                            width: "120px",
                                            height: "33.6px",
                                            children: /*#__PURE__*/ _jsx("g", {
                                                id: "Слой_2",
                                                "data-name": "Слой 2",
                                                children: /*#__PURE__*/ _jsxs("g", {
                                                    id: "Слой_1-2",
                                                    "data-name": "Слой 1",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("path", {
                                                            fill: "#ff9f24",
                                                            d: "M48.8 165.91h13.05a22.78 22.78 0 0 0 16.36-6.6 21.47 21.47 0 0 0 6.86-16.09A21.9 21.9 0 0 0 78.21 127a22.57 22.57 0 0 0-16.36-6.72H0V9h51.57q22.82 0 39.1 16.29T107 64.36a53.5 53.5 0 0 1-9.8 31.12 57.7 57.7 0 0 1 16.42 19.92 55.6 55.6 0 0 1 6 25.45q0 23.86-16.75 40.55a55.25 55.25 0 0 1-40.49 16.69H48.8Zm-15.3-82H50a21.65 21.65 0 0 0 15-6.19A20.46 20.46 0 0 0 71.75 62a19.78 19.78 0 0 0-6.66-15.36 22 22 0 0 0-15.1-6H33.5ZM151.93 9h37.85v189.09h-37.85Zm110.65 0v151.5H333v37.59H224.73V9Zm143.36 0v151.5h70.43v37.59H368.09V9Z"
                                                        }),
                                                        /*#__PURE__*/ _jsx("path", {
                                                            d: "m562.23 118.43-1.32-.27q-17.68-4.47-28.62-11.86-23.22-15.3-24.4-42.74-1.18-22.81 13.85-38.64a55.7 55.7 0 0 1 21.89-14.18q12.52-4.41 22.16-4.41A103 103 0 0 1 584 7.84a70 70 0 0 1 15.76 4.75q7.32 3.24 11.6 5.67t11.15 6.93l6.2 4.08-19.8 32.18-6.72-4.35q-5.94-3.83-8.77-5.47a81 81 0 0 0-7.59-3.76A39.7 39.7 0 0 0 576.4 45a64 64 0 0 0-10.61-.79 28 28 0 0 0-8.18 1.79 21.1 21.1 0 0 0-8.61 5.17q-3.57 3.55-3.3 10.81a14.55 14.55 0 0 0 5.67 11.67q5.54 4.42 18.6 7.72 1.05.27 5.41 1.71 27.42 9.38 41.93 25.06 14.77 16.35 14.78 37.06a52.4 52.4 0 0 1-4 19.72 59.7 59.7 0 0 1-11.08 17.74 57.67 57.67 0 0 1-43.52 19.52 113.7 113.7 0 0 1-33.24-4.62 104.44 104.44 0 0 1-45.1-28.62l27.56-26q20.06 21.36 50.78 21.37a20.8 20.8 0 0 0 9.82-2.25 17.35 17.35 0 0 0 6.53-5.54 30 30 0 0 0 3.37-6.26 15 15 0 0 0 1.12-5.08q0-7.65-8.25-14.51t-23.14-12ZM652.3 9h143v37.82h-52.52v151.53h-37.72V46.82H652.3Zm488.24 37.55h-36.27v151.54h-37.72V8.7h74a54.55 54.55 0 0 1 40.1 16.68 55.06 55.06 0 0 1 16.61 40.3 55.24 55.24 0 0 1-9.1 30.92 56.07 56.07 0 0 1-24.16 20.77l37.58 81h-40.35l-35.35-75.83V84.67h14.64a18.1 18.1 0 0 0 13.39-5.54 18.43 18.43 0 0 0 5.47-13.45 18.63 18.63 0 0 0-5.47-13.52 18 18 0 0 0-13.37-5.61M1355.78 9v37.82h-126.21V9Zm-126.21 110.75V81.9h104.58v37.85Zm0 78.6V160.5h125.29v37.85Z"
                                                        }),
                                                        /*#__PURE__*/ _jsx("path", {
                                                            fill: "#ff9f24",
                                                            d: "M1033.14 103.76c0 46-40.05 88.53-84.31 96.26-8.44 1.41-28.46 2.11-38.65 1.06-34.08-2.81-62.53-17.92-76.94-40.05-11.24-16.87-16.16-37.95-14.4-59.73 6-57.61 37.94-94.5 98-100.83a109 109 0 0 1 33.37 2.11c17.57 3.52 34.08 12 48.83 25.3 23.56 21.12 33.75 43.91 34.1 75.88M855 194.4a37.4 37.4 0 0 0-7.38-4.21c-1.41 0-9.49-5.63-13.7-9.84-9.14-8.43-12.65-14.4-9.14-15.46 2.11-.7 3.51.35 15.46 9.84 14.05 11.59 18.27 14.75 22.13 17.56 3.52 2.46 4.57 4.57 2.46 5.63-2.08.7-2.78.35-9.83-3.52m165.14-101.88-2.81-6c-10.18-23.19-26.7-36.54-55.15-44.62-10.18-3.13-16.87-3.48-23.18-1.73-2.81.71-9.13 2.11-14 2.81-11.6 2.11-20.38 4.57-25.3 7.38-8.78 4.92-17.21 11.24-20.73 15.81C865.21 83 858.19 115 863.81 136.08a105 105 0 0 0 4.21 11.6c2.81 5.62 9.49 13 15.81 16.86 16.16 11.24 39.35 18.27 59.38 18.27 16.51 0 29.51-5.27 42.86-15.46 20-15.11 33.72-38.64 33.72-63.94Zm-117.69 26.7c-9.48 3.16-17.56 4.92-23.18 4.92a17.6 17.6 0 0 1-6.68-1.06c-6-2.81-7.38-13.35-3.51-26.34 3.16-10.19 8.43-24.95 21.43-24.95 10.54 0 26.35 19.32 30.56 27.4 2.46 4.92 2.81 7 1.76 9.84q-2.12 4.74-8.43 6.33c-2.11.7-7.4 2.45-11.95 3.86m-29.51 82.56c-1.75-1.41-1.05-3.51 1.06-3.51 1.75 0 3.86 1.75 3.51 3.16-.7 1.4-2.81 1.4-4.57.35m24.24-88.18a120 120 0 0 1 12-3.51c7.73-2.11 7.73-3.17.35-12.65-9.84-12.65-15.81-18.62-19-18.62-2.81 0-5.62 2.81-8.78 9.48-5.63 11.25-8.08 24.25-5.63 27.76 1.74 1.75 11.58.7 21.06-2.46m28.11 4.57c4.57 0 4.57 4.91 4.21 17.21-1 23.54-3.86 32.32-11.24 35.84-3.16 1.4-4.21 1.75-7 .7-6-1.76-14.76-7.73-21.43-14.41-7-7.73-8.44-11.59-5.63-17.56 2.46-5.27 7-8.43 25.65-16.51 7.71-3.17 12.28-5.27 15.44-5.27m-2.46 5.62c-.35-.35-2.11.7-4.57 2.1s-8.43 4.57-13.7 7c-10.19 4.92-14.4 8.08-15.11 11.59-.35 2.11 0 2.82 3.17 6.33a81 81 0 0 0 15.45 11.94c4.92 2.81 6.33 3.17 7.73 1.41a18.5 18.5 0 0 0 3.52-8.78c1.05-4.19 3.86-31.24 3.51-31.59m-7-39.7C907.37 69 906.67 63 913 56.68c5.27-5.26 20.73-9.83 28.46-9.83 3.86 0 6.32 1.4 8.08 4.57s1.76 10.18-.35 15.81c-2.12 7.37-7.39 18.26-10.19 22.83-4.22 6.32-7.38 7.73-13.7 6.32-3.17-.7-3.17-.7-9.5-12.29Zm7.73-9.49c1.76 3.87 3.87 9.14 4.92 12.3.7 2.81 2.11 5.27 2.46 5.27s1.76-1.41 3.16-3.16c2.46-3.52 7.38-16.16 9.14-22.84 1.4-5.62 1-10.19-.71-11.59-1.75-1.06-18.27 4.92-21.08 7.73-2.13 2.1-1.45 4.92 2.08 12.29Zm21.08 36.19c-.35 3.16-2.11 5.27-5.27 6.67s-4.21 1.06-8.08-.35c-6.67-3.16-8.08-10.89-2.46-14.4a13.4 13.4 0 0 1 5.62-1.06 15.9 15.9 0 0 1 6.33 1.06c3.13 1.05 4.53 4.56 3.83 8.08ZM956.2 135c11.95 14.4 15.11 19.32 15.11 23.89 0 10.19-14 20.38-26 20.38a20.6 20.6 0 0 1-6.67-1.41c-3.51-1.76-6.68-5.27-7.38-8.78-.7-3.87 0-16.16 1.76-28.11 1.05-5.62 1.75-11.94 2.11-14 .7-4.57 2.45-6.32 6.32-6.32 3.16.35 3.55.68 14.75 14.35m-6 5.27-7.73-10.89-.7 4.56c-.35 2.46-.7 7.38-1 10.9-2.46 18.62-2.46 22.83-.36 25.29 1.41 1.41 6 1.41 10.19-.35 4.57-2.11 9.49-7 10.19-10.19s-.72-5.94-10.56-19.29Zm-8.4-43.89c.7-2.45 8.08-16.16 13-24.24 7.38-11.94 14.05-17.56 21.78-17.56 5.27 0 11.24 2.46 18.27 7.37 11.59 8.44 12.3 15.81 2.81 24.6-10.54 9.48-44.27 20-53 16.51-2.16-.7-3.56-4.57-2.86-6.68m74.48 17.92c-1.41 14.41-13.7 35.48-30.56 35.48a16.5 16.5 0 0 1-6-1.05c-4.56-1.76-11.94-7.38-20.72-16.16-11.25-11.24-14.06-16.51-10.55-20 1.76-2.11 4.22-3.17 16.87-7 6-1.76 15.11-4.57 20.73-6.33 7.37-2.81 12.64-4.21 16.51-4.21a10.8 10.8 0 0 1 6 1.4c5.96 3.12 8.77 9.44 7.72 17.87M954.1 95c.35 0 6-1.41 12.64-2.81C982.9 88 992 84.44 995.9 80.57c4.57-3.86 4.22-5.62-.35-9.83S982.2 62 979.39 62a8.6 8.6 0 0 0-4.21 1.41c-.71.7-6 7.38-11.25 15.11C954.45 92.87 953 95 954.1 95m50.24 12.29-.36-3.86-2.81-.35q-2.62 0-11.59 3.16c-2.81 1-32 10.19-34.08 10.19-.7.7 5.62 8.78 12.3 15.11 6 6.32 13.35 10.89 16.51 10.89.7.35 2.46-.36 3.51-1.06 7.03-4.58 16.52-25.66 16.52-34.1ZM982.9 8.55c0-2.1 2.46-4.21 4.92-4.21.71 0 3.16 1 5.62 2.11 4.92 2.81 16.16 13.35 21.79 20.72 3.86 5.27 11.24 18.27 10.54 19-1.41 1.4-3.17.35-4.92-3.17-3.52-6-16.51-17.91-25.3-23.18-9.83-5.64-12.65-8.1-12.65-11.27m43.92 47.08c-.7-2.11-.35-3.16 1.4-3.16 1.06 0 2.46 2.46 2.46 4.21s-2.81 1.06-3.86-1.05"
                                                        })
                                                    ]
                                                })
                                            })
                                        })
                                    })
                                })
                            }),
                            !isMobile && /*#__PURE__*/ _jsxs(WrapperNavigation, {
                                children: [
                                    /*#__PURE__*/ _jsx(NavigationText, {
                                        variant: "h6",
                                        color: colors.text.main,
                                        $isActive: activeHeader === headerLinks.home,
                                        children: /*#__PURE__*/ _jsx(StyledLink, {
                                            onClick: ()=>setActiveHeader(headerLinks.home),
                                            href: "/".concat(lng, "/application"),
                                            children: t("home")
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(NavigationText, {
                                        variant: "h6",
                                        color: colors.text.main,
                                        $isActive: activeHeader === headerLinks.customers,
                                        children: /*#__PURE__*/ _jsx(StyledLink, {
                                            onClick: ()=>setActiveHeader(headerLinks.customers),
                                            href: "/".concat(lng, "/application/customers"),
                                            sx: {
                                                color: "#000"
                                            },
                                            children: t("customers")
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(NavigationText, {
                                        variant: "h6",
                                        color: colors.text.main,
                                        $isActive: activeHeader === headerLinks.invoices,
                                        children: /*#__PURE__*/ _jsx(StyledLink, {
                                            onClick: ()=>setActiveHeader(headerLinks.invoices),
                                            href: "/".concat(lng, "/application/invoices"),
                                            sx: {
                                                color: "#000"
                                            },
                                            children: t("invoices")
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(NavigationText, {
                                        variant: "h6",
                                        color: colors.text.main,
                                        $isActive: activeHeader === headerLinks.products,
                                        children: /*#__PURE__*/ _jsx(StyledLink, {
                                            onClick: ()=>setActiveHeader(headerLinks.products),
                                            href: "/".concat(lng, "/application/products"),
                                            sx: {
                                                color: "#000"
                                            },
                                            children: t("products")
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(NavigationText, {
                                        variant: "h6",
                                        color: colors.text.main,
                                        $isActive: activeHeader === headerLinks.xrechnung,
                                        children: /*#__PURE__*/ _jsx(StyledLink, {
                                            onClick: (event)=>handleXrechnungNavigation(event),
                                            href: "/".concat(lng, "/application/xrechnung-visu"),
                                            sx: {
                                                color: "#000"
                                            },
                                            children: t("xrechnung")
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(WrapperNavigation, {
                        $isNotNavigation: true,
                        alignItems: "center",
                        children: [
                            isDesktop && /*#__PURE__*/ _jsxs(Box, {
                                display: "flex",
                                gap: "6px",
                                alignItems: "center",
                                children: [
                                    /*#__PURE__*/ _jsx(Tooltip, {
                                        title: userEmailTooltip,
                                        placement: "bottom",
                                        children: /*#__PURE__*/ _jsx(IconButton, {
                                            sx: {
                                                padding: "unset"
                                            },
                                            onClick: ()=>{
                                                setActiveHeader(headerLinks.myaccount);
                                                router.push("/".concat(lng, "/application/myaccount"));
                                            },
                                            children: /*#__PURE__*/ _jsxs(AccountIcon, {
                                                children: [
                                                    /*#__PURE__*/ _jsx("path", {
                                                        d: "M10.2687 10.0916C7.76263 10.0916 5.7229 8.05182 5.7229 5.54578C5.7229 3.03974 7.76263 1 10.2687 1C12.7747 1 14.8145 3.03974 14.8145 5.54578C14.8145 8.05182 12.7774 10.0916 10.2687 10.0916ZM10.2687 2.08804C8.36256 2.08804 6.81367 3.63966 6.81367 5.54305C6.81367 7.44644 8.36528 8.99806 10.2687 8.99806C12.1748 8.99806 13.7237 7.44644 13.7237 5.54305C13.7237 3.63966 12.1748 2.08804 10.2687 2.08804Z",
                                                        fill: "#FF9F24"
                                                    }),
                                                    /*#__PURE__*/ _jsx("path", {
                                                        d: "M5.78627 9.70427C6.50345 9.49157 7.28881 9.69609 7.83692 10.2387C8.48865 10.885 9.35309 11.2423 10.2721 11.2423C11.1556 11.2423 11.9955 10.9096 12.639 10.3015C13.228 9.7479 14.0734 9.57065 14.7878 9.85425C16.8603 10.6723 18.1992 12.6439 18.1992 14.8718V15.8998C18.1992 17.6969 16.7376 19.1585 14.9405 19.1585H5.1918C3.39476 19.1585 1.93313 17.6969 1.93313 15.8998V14.8718C1.9304 12.5021 3.51748 10.3778 5.78627 9.70427ZM3.02118 15.8998C3.02118 17.0942 3.99469 18.0677 5.18908 18.0677H14.9378C16.1322 18.0677 17.1057 17.0942 17.1057 15.8998V14.8718C17.1057 13.0938 16.0368 11.5231 14.3843 10.8687C14.2861 10.8305 14.1797 10.8114 14.0734 10.8114C13.8307 10.8114 13.5798 10.9123 13.3835 11.095C12.5381 11.8913 11.431 12.3303 10.2693 12.3303C9.06131 12.3303 7.92418 11.8613 7.06793 11.0105C6.80341 10.7487 6.43255 10.6478 6.09714 10.746C4.28647 11.2832 3.0239 12.9793 3.0239 14.8691V15.8998H3.02118Z",
                                                        fill: "#FF9F24"
                                                    })
                                                ]
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(NavigationText, {
                                        variant: "h6",
                                        color: colors.text.main,
                                        $isActive: activeHeader === headerLinks.myaccount,
                                        children: /*#__PURE__*/ _jsx(StyledLink, {
                                            onClick: ()=>setActiveHeader(headerLinks.myaccount),
                                            href: "/".concat(lng, "/application/myaccount"),
                                            sx: {
                                                color: "#000"
                                            },
                                            children: t("myaccount")
                                        })
                                    })
                                ]
                            }),
                            (isDesktop || isTablet) && /*#__PURE__*/ _jsx(Tooltip, {
                                title: renderSubscriptionTooltip(),
                                placement: "bottom",
                                children: /*#__PURE__*/ _jsx(IconButton, {
                                    sx: {
                                        padding: 0
                                    },
                                    children: /*#__PURE__*/ _jsx(NotificationsActiveIcon, {
                                        htmlColor: "#FF9F24"
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(StyledLanguageButton, {
                                id: "demo-customized-button",
                                startIcon: startIcon,
                                "aria-controls": open ? "demo-customized-menu" : undefined,
                                "aria-haspopup": "true",
                                "aria-expanded": open ? "true" : undefined,
                                variant: "contained",
                                disableElevation: true,
                                disableRipple: true,
                                onClick: handleClick,
                                endIcon: /*#__PURE__*/ _jsx(KeyboardArrowDownIcon, {}),
                                children: (params === null || params === void 0 ? void 0 : params.lng) === germanLng ? "DEU" : "ENG"
                            }),
                            isTablet && !isDesktop && /*#__PURE__*/ _jsxs(Box, {
                                display: "flex",
                                gap: "6px",
                                alignItems: "center",
                                children: [
                                    /*#__PURE__*/ _jsx(Tooltip, {
                                        title: userEmailTooltip,
                                        placement: "bottom",
                                        children: /*#__PURE__*/ _jsxs(AccountIcon, {
                                            children: [
                                                /*#__PURE__*/ _jsx("path", {
                                                    d: "M10.2687 10.0916C7.76263 10.0916 5.7229 8.05182 5.7229 5.54578C5.7229 3.03974 7.76263 1 10.2687 1C12.7747 1 14.8145 3.03974 14.8145 5.54578C14.8145 8.05182 12.7774 10.0916 10.2687 10.0916ZM10.2687 2.08804C8.36256 2.08804 6.81367 3.63966 6.81367 5.54305C6.81367 7.44644 8.36528 8.99806 10.2687 8.99806C12.1748 8.99806 13.7237 7.44644 13.7237 5.54305C13.7237 3.63966 12.1748 2.08804 10.2687 2.08804Z",
                                                    fill: "#FF9F24"
                                                }),
                                                /*#__PURE__*/ _jsx("path", {
                                                    d: "M5.78627 9.70427C6.50345 9.49157 7.28881 9.69609 7.83692 10.2387C8.48865 10.885 9.35309 11.2423 10.2721 11.2423C11.1556 11.2423 11.9955 10.9096 12.639 10.3015C13.228 9.7479 14.0734 9.57065 14.7878 9.85425C16.8603 10.6723 18.1992 12.6439 18.1992 14.8718V15.8998C18.1992 17.6969 16.7376 19.1585 14.9405 19.1585H5.1918C3.39476 19.1585 1.93313 17.6969 1.93313 15.8998V14.8718C1.9304 12.5021 3.51748 10.3778 5.78627 9.70427ZM3.02118 15.8998C3.02118 17.0942 3.99469 18.0677 5.18908 18.0677H14.9378C16.1322 18.0677 17.1057 17.0942 17.1057 15.8998V14.8718C17.1057 13.0938 16.0368 11.5231 14.3843 10.8687C14.2861 10.8305 14.1797 10.8114 14.0734 10.8114C13.8307 10.8114 13.5798 10.9123 13.3835 11.095C12.5381 11.8913 11.431 12.3303 10.2693 12.3303C9.06131 12.3303 7.92418 11.8613 7.06793 11.0105C6.80341 10.7487 6.43255 10.6478 6.09714 10.746C4.28647 11.2832 3.0239 12.9793 3.0239 14.8691V15.8998H3.02118Z",
                                                    fill: "#FF9F24"
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(NavigationText, {
                                        variant: "h6",
                                        color: colors.text.main,
                                        $isActive: activeHeader === headerLinks.myaccount,
                                        children: /*#__PURE__*/ _jsx(StyledLink, {
                                            onClick: ()=>setActiveHeader(headerLinks.myaccount),
                                            href: "/".concat(lng, "/application/myaccount"),
                                            sx: {
                                                color: "#000"
                                            },
                                            children: t("myaccount")
                                        })
                                    })
                                ]
                            }),
                            isMobile && /*#__PURE__*/ _jsx(Tooltip, {
                                title: userEmailTooltip,
                                placement: "bottom",
                                children: /*#__PURE__*/ _jsx(IconButton, {
                                    sx: {
                                        padding: "unset"
                                    },
                                    onClick: ()=>{
                                        setActiveHeader(headerLinks.myaccount);
                                        router.push("/".concat(lng, "/application/myaccount"));
                                    },
                                    children: /*#__PURE__*/ _jsxs(AccountIcon, {
                                        children: [
                                            /*#__PURE__*/ _jsx("path", {
                                                d: "M10.2687 10.0916C7.76263 10.0916 5.7229 8.05182 5.7229 5.54578C5.7229 3.03974 7.76263 1 10.2687 1C12.7747 1 14.8145 3.03974 14.8145 5.54578C14.8145 8.05182 12.7774 10.0916 10.2687 10.0916ZM10.2687 2.08804C8.36256 2.08804 6.81367 3.63966 6.81367 5.54305C6.81367 7.44644 8.36528 8.99806 10.2687 8.99806C12.1748 8.99806 13.7237 7.44644 13.7237 5.54305C13.7237 3.63966 12.1748 2.08804 10.2687 2.08804Z",
                                                fill: "#FF9F24"
                                            }),
                                            /*#__PURE__*/ _jsx("path", {
                                                d: "M5.78627 9.70427C6.50345 9.49157 7.28881 9.69609 7.83692 10.2387C8.48865 10.885 9.35309 11.2423 10.2721 11.2423C11.1556 11.2423 11.9955 10.9096 12.639 10.3015C13.228 9.7479 14.0734 9.57065 14.7878 9.85425C16.8603 10.6723 18.1992 12.6439 18.1992 14.8718V15.8998C18.1992 17.6969 16.7376 19.1585 14.9405 19.1585H5.1918C3.39476 19.1585 1.93313 17.6969 1.93313 15.8998V14.8718C1.9304 12.5021 3.51748 10.3778 5.78627 9.70427ZM3.02118 15.8998C3.02118 17.0942 3.99469 18.0677 5.18908 18.0677H14.9378C16.1322 18.0677 17.1057 17.0942 17.1057 15.8998V14.8718C17.1057 13.0938 16.0368 11.5231 14.3843 10.8687C14.2861 10.8305 14.1797 10.8114 14.0734 10.8114C13.8307 10.8114 13.5798 10.9123 13.3835 11.095C12.5381 11.8913 11.431 12.3303 10.2693 12.3303C9.06131 12.3303 7.92418 11.8613 7.06793 11.0105C6.80341 10.7487 6.43255 10.6478 6.09714 10.746C4.28647 11.2832 3.0239 12.9793 3.0239 14.8691V15.8998H3.02118Z",
                                                fill: "#FF9F24"
                                            })
                                        ]
                                    })
                                })
                            }),
                            (isDesktop || isTablet) && /*#__PURE__*/ _jsx(NavigationText, {
                                variant: "h6",
                                color: colors.text.main,
                                children: /*#__PURE__*/ _jsx(StyledLink, {
                                    onClick: handleSignOut,
                                    href: "",
                                    sx: {
                                        color: "#000"
                                    },
                                    children: t("logout")
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(StyledMenu, {
                        id: "language-menu",
                        MenuListProps: {
                            "aria-labelledby": "language-button"
                        },
                        anchorEl: anchorEl,
                        open: open,
                        onClose: handleClose,
                        children: [
                            /*#__PURE__*/ _jsxs(MenuItem, {
                                onClick: ()=>handleClose("en"),
                                sx: {
                                    fontSize: "14px",
                                    fontWeight: "400",
                                    color: "#000"
                                },
                                disableRipple: true,
                                children: [
                                    /*#__PURE__*/ _jsx(Image, {
                                        style: {
                                            marginRight: "7px"
                                        },
                                        src: "/eng.png",
                                        alt: "eng",
                                        width: "21",
                                        height: "12"
                                    }),
                                    "ENG"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(MenuItem, {
                                onClick: ()=>handleClose("de"),
                                disableRipple: true,
                                children: [
                                    /*#__PURE__*/ _jsx(Image, {
                                        style: {
                                            marginRight: "7px"
                                        },
                                        src: GermanFlag,
                                        alt: "deu",
                                        width: "21",
                                        height: "12"
                                    }),
                                    "DEU"
                                ]
                            })
                        ]
                    })
                ]
            }),
            showDrawer && /*#__PURE__*/ _jsx(Box, {
                position: "absolute",
                zIndex: "1000",
                sx: {
                    backgroundColor: "#FFF"
                },
                height: "calc(100vh - 52px)",
                width: "100%",
                children: /*#__PURE__*/ _jsx(Box, {
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    padding: "0",
                    textAlign: "center",
                    sx: {
                        transform: "translate(-50%, -50%)"
                    },
                    children: /*#__PURE__*/ _jsxs(Box, {
                        display: "flex",
                        flexDirection: "column",
                        gap: "30px",
                        children: [
                            /*#__PURE__*/ _jsx(DrawerText, {
                                variant: "h5",
                                color: colors.text.main,
                                $isActive: activeHeader === headerLinks.home,
                                children: /*#__PURE__*/ _jsx(StyledLink, {
                                    onClick: ()=>handleMobileNavigation(headerLinks.home),
                                    href: "/".concat(lng, "/application"),
                                    children: t("home")
                                })
                            }),
                            /*#__PURE__*/ _jsx(DrawerText, {
                                variant: "h5",
                                color: colors.text.main,
                                $isActive: activeHeader === headerLinks.customers,
                                children: /*#__PURE__*/ _jsx(StyledLink, {
                                    onClick: ()=>handleMobileNavigation(headerLinks.customers),
                                    href: "/".concat(lng, "/application/customers"),
                                    sx: {
                                        color: "#000"
                                    },
                                    children: t("customers")
                                })
                            }),
                            /*#__PURE__*/ _jsx(DrawerText, {
                                variant: "h5",
                                color: colors.text.main,
                                $isActive: activeHeader === headerLinks.invoices,
                                children: /*#__PURE__*/ _jsx(StyledLink, {
                                    onClick: ()=>handleMobileNavigation(headerLinks.invoices),
                                    href: "/".concat(lng, "/application/invoices"),
                                    sx: {
                                        color: "#000"
                                    },
                                    children: t("invoices")
                                })
                            }),
                            /*#__PURE__*/ _jsx(DrawerText, {
                                variant: "h5",
                                color: colors.text.main,
                                $isActive: activeHeader === headerLinks.products,
                                children: /*#__PURE__*/ _jsx(StyledLink, {
                                    onClick: ()=>handleMobileNavigation(headerLinks.products),
                                    href: "/".concat(lng, "/application/products"),
                                    sx: {
                                        color: "#000"
                                    },
                                    children: t("products")
                                })
                            }),
                            /*#__PURE__*/ _jsx(DrawerText, {
                                variant: "h5",
                                color: colors.text.main,
                                $isActive: activeHeader === headerLinks.xrechnung,
                                children: /*#__PURE__*/ _jsx(StyledLink, {
                                    onClick: (event)=>handleXrechnungNavigation(event, true),
                                    href: "/".concat(lng, "/application/xrechnung-visu"),
                                    sx: {
                                        color: "#000"
                                    },
                                    children: t("xrechnung")
                                })
                            }),
                            /*#__PURE__*/ _jsx(DrawerText, {
                                variant: "h5",
                                color: colors.text.main,
                                $isActive: activeHeader === headerLinks.myaccount,
                                children: /*#__PURE__*/ _jsx(StyledLink, {
                                    onClick: ()=>handleMobileNavigation(headerLinks.myaccount),
                                    href: "/".concat(lng, "/application/myaccount"),
                                    sx: {
                                        color: "#000"
                                    },
                                    children: t("myaccount")
                                })
                            }),
                            /*#__PURE__*/ _jsx(DrawerText, {
                                variant: "h5",
                                color: colors.text.main,
                                children: /*#__PURE__*/ _jsx(StyledLink, {
                                    onClick: handleSignOut,
                                    href: "",
                                    sx: {
                                        color: "#000"
                                    },
                                    children: t("logout")
                                })
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(SubscriptionWarningModal, {
                open: showSubscriptionModal,
                onClose: ()=>setShowSubscriptionModal(false),
                lng: (_params_lng_toString1 = params === null || params === void 0 ? void 0 : (_params_lng1 = params.lng) === null || _params_lng1 === void 0 ? void 0 : _params_lng1.toString()) !== null && _params_lng_toString1 !== void 0 ? _params_lng_toString1 : "en",
                context: "xrechnung"
            })
        ]
    });
};
export default Header;
